import React, { useContext, useState, useRef, useEffect } from "react"
import moment from "moment"
import ServiceCardBannerOverlay from "../services/ServiceCardBannerOverlay"
import useLoading from "../../hooks/useLoading"
import { Badge } from "../ui/badge"
import { Button } from "../ui/button"
import { CoachPrivateProfileContext } from "../../pages/manage-business/profile/context/coachPrivateProfileContext"
import { getOrdinal } from "../../utils/get-ordinal"
import { getLightTextColor } from "../../utils/getLightTextColor"
import { getPrice } from "../../utils/services/get-price"
import { getTextColor } from "../../utils/getTextColor"
import { getUnits } from "../../utils/services/get-units/get-units"
import { pluralize } from "../../utils/pluralize"
import { SvgCheckCircle, SvgEdit, SvgShare } from "../icons/"
import { useNavigate } from "react-router-dom"
import { useToast } from "../../hooks/useToast"

interface Props {
  service?: any
  getServices?: any
}

export default function PrivateProfileServiceCard({ service }: Props) {
  const { coachPublicProfileData } = useContext(CoachPrivateProfileContext)
  const { startLoading } = useLoading()
  const { is_group_service } = service
  const { toast } = useToast()
  const [isHovered, setIsHovered] = useState(false)
  const [showRecurrencePopover, setShowRecurrencePopover] = useState(false)
  const serviceDetails = service?.service_details
  const navigate = useNavigate()
  const coachColor = coachPublicProfileData?.profile_information?.profileColor
  const textColor = getLightTextColor(coachColor)
  const recurrencePopoverRef = useRef<HTMLDivElement>(null)
  const recurrenceTriggerRef = useRef<HTMLButtonElement>(null)

  const getProfileColorClass = () => {
    return `text-[#${coachPublicProfileData?.profile_information?.profileColor}]`
  }

  const getSessionCount = () => {
    if (["consultation", "individual"].includes(serviceDetails?.type)) {
      return `${serviceDetails?.session.count} session`
    } else if (["bundle"].includes(serviceDetails?.type)) {
      return pluralize(serviceDetails?.session.count, "session")
    } else if (["recurring"].includes(serviceDetails?.type)) {
      return (
        <div>
          {pluralize(serviceDetails?.session?.count, "session")}
          <span> / {`${serviceDetails?.billing?.unit}`}</span>
        </div>
      )
    } else {
      return "Not Found"
    }
  }

  const goToEditService = () => {
    startLoading()
    navigate(`/coach/services`, {
      state: { service: service, flag: true },
    })
  }

  const getStartDate = (): { startMessage: string } => {
    const meetingDates = serviceDetails?.group?.meetingDates || []
    const userTz = moment.tz.guess()

    const upcomingDates = meetingDates
      .map((date: string, index: number) => {
        const startDate = new Date(date)
        const sessionIndex = index + 1
        const formattedDate = moment
          .tz(startDate, userTz)
          .format("MMM DD [at] h:mm A z")
        return {
          startDate,
          sessionIndex,
          formattedDate,
        }
      })
      .filter((session: any) => session.startDate >= new Date())
      .sort((a: any, b: any) => a.startDate.getTime() - b.startDate.getTime())

    if (upcomingDates.length === 0) {
      return { startMessage: "No upcoming sessions" }
    } else {
      const nextSession = upcomingDates[0]
      const secondSession = upcomingDates
        .slice(1)
        .find((session: any) => session.startDate >= new Date())

      let startMessage = `${getOrdinal(
        nextSession.sessionIndex
      )} Session Scheduled For ${nextSession.formattedDate}`

      if (secondSession) {
        startMessage = `${getOrdinal(
          secondSession.sessionIndex
        )} Session Scheduled For ${secondSession.formattedDate}`
      }
      return { startMessage }
    }
  }

  const getRecurrenceSchedule = (): { recurrenceMessages: string[] } => {
    const weeklyDayTimes = serviceDetails?.group?.weeklyDayTimes || []
    const expireType = serviceDetails?.expiration?.type

    const userTz = moment.tz.guess()

    const getDayName = (dayIndex: number): string =>
      moment()
        .isoWeekday(dayIndex === 0 ? 1 : dayIndex + 1)
        .format("dddd")

    const getTimeWithZone = (day: number, time: string): string => {
      let base = moment.tz(userTz).startOf("day")

      const targetIsoDay = day === 0 ? 1 : day + 1
      while (base.isoWeekday() !== targetIsoDay) {
        base = base.add(1, "day")
      }

      const [hour, minute] = time.split(":").map(Number)
      base = base.hour(hour).minute(minute)

      return base.format("h:mm A z")
    }

    const getMonthlyPrefix = (freq: number): string => {
      const ordinals = ["First", "Second", "Third", "Fourth", "Last"]
      return ordinals[freq] || ""
    }

    const scheduleStrings = weeklyDayTimes.map(({ day, freq, time }: any) => {
      const dayName = getDayName(day)
      const formattedTime = getTimeWithZone(day, time)

      if (expireType === "months") {
        const prefix = getMonthlyPrefix(freq)
        return `Recurs: ${prefix} ${dayName} @ ${formattedTime}`
      } else {
        return `Recurs: ${dayName} @ ${formattedTime}`
      }
    })

    return { recurrenceMessages: scheduleStrings }
  }

  const copyShareLink = () => {
    const base = process.env.REACT_APP_BASE_URL
    const shareLink = `${base}/find-a-coach/${coachPublicProfileData.endpoint_slug}/services/${service?.service_id}`
    const freeConsutlationShareLink = `${base}/${coachPublicProfileData.endpoint_slug}/${service?.service_id}`
    if (
      service?.service_details.type === "consultation" &&
      service?.service_details.billing.amount === "0"
    ) {
      navigator.clipboard.writeText(freeConsutlationShareLink)
    } else {
      navigator.clipboard.writeText(shareLink)
    }
    toast.success("Copied link to clipboard.")
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        recurrencePopoverRef.current &&
        recurrenceTriggerRef.current &&
        !recurrencePopoverRef.current.contains(event.target as Node) &&
        !recurrenceTriggerRef.current.contains(event.target as Node)
      ) {
        setShowRecurrencePopover(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  return (
    <div
      className={`w-full md:w-[385px] bg-white rounded-[16px] main-shadow min-h-[410px] relative shadow-custom last:mb-[80px] md:last:mb-0 flex flex-col
    transition-transform duration-300 cursor-pointer
    ${isHovered ? "scale-105 z-[50]" : "scale-100 z-[10]"}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={goToEditService}
    >
      {" "}
      <div
        className="w-full h-[110px] border border-white border-[12px] rounded-[20px] relative"
        style={{
          background: serviceDetails.image.exists
            ? `url(https://clientservice-images-${
                process.env.REACT_APP_USER_ENVIRONMENT
              }.s3.amazonaws.com/${
                service.stripe_product_id
              }?t=${new Date().getTime()}) center/cover no-repeat`
            : coachColor,
          borderWidth: "12px",
        }}
      >
        <ServiceCardBannerOverlay service={service} />
      </div>
      <div className="w-full flex flex-col px-[16px] py-[12px] flex-1 -mt-4">
        <div className="w-full flex flex-col items-left justify-between">
          <h4
            className={`font-bold break-words w-full ${
              (serviceDetails?.title?.length ?? 0) > 50
                ? "text-[14px]"
                : "text-[18px]"
            }`}
          >
            {serviceDetails?.title}
          </h4>
          <h3 className={`font-bold text-[16px] ${getProfileColorClass()}`}>
            {getPrice(
              parseInt(serviceDetails?.billing?.amount),
              serviceDetails?.symbol
            )}
            <span className="text-graySlate text-[12px] text-base font-normal">
              {getUnits(
                serviceDetails.billing.unit,
                parseInt(serviceDetails?.billing?.amount),
                serviceDetails?.type
              )}
            </span>
            <div className="flex flex-col gap-1 mt-2">
              {is_group_service &&
                serviceDetails?.type === "recurring" &&
                (() => {
                  const recurrenceMessages =
                    getRecurrenceSchedule().recurrenceMessages

                  return recurrenceMessages.length > 1 ? (
                    <div className="mt-2">
                      <button
                        ref={recurrenceTriggerRef}
                        className="text-blurple text-sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          setShowRecurrencePopover((prev) => !prev)
                        }}
                      >
                        Click here to see scheduled dates
                      </button>

                      {showRecurrencePopover && (
                        <div
                          ref={recurrencePopoverRef}
                          className="absolute z-10 mt-2 p-3 bg-white border rounded shadow-md whitespace-nowrap"
                        >
                          {recurrenceMessages.map((msg, idx) => (
                            <div key={idx} className="mb-1 font-normal text-sm">
                              {msg}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="">
                      <Badge variant="success">{recurrenceMessages[0]}</Badge>
                    </div>
                  )
                })()}

              {is_group_service &&
                serviceDetails?.type !== "recurring" &&
                getStartDate().startMessage && (
                  <div className="mt-1 w-fit">
                    <Badge variant="success">
                      {getStartDate().startMessage}
                    </Badge>
                  </div>
                )}
            </div>
          </h3>
        </div>
        <div className="w-full flex items-center justify-start">
          <div className="flex gap-[16px] items-center mt-2">
            <div
              className="py-[4px] px-[8px] rounded-[8px] flex items-center justify-center font-bold text-base text-black"
              style={{ background: textColor }}
            >
              {getSessionCount()}
            </div>
            <div
              className="py-[4px] px-[8px] rounded-[8px] flex items-center justify-center font-bold text-base text-black"
              style={{ background: textColor }}
            >
              <div>
                {parseInt(serviceDetails?.session?.duration) / 60} minutes
              </div>
            </div>
          </div>
        </div>
        <div className="text-sm mt-2 flex-1">
          <div className="flex items-center">
            {serviceDetails?.details?.features?.feature1 ? (
              <>
                <span>
                  <SvgCheckCircle
                    fill={`${coachPublicProfileData?.profile_information?.profileColor}`}
                  />
                </span>
                <p className="ml-3 line-clamp-1 mt-1">
                  {serviceDetails.details.features.feature1}
                </p>
              </>
            ) : null}
          </div>
          <div className="flex items-center">
            {serviceDetails?.details?.features?.feature2 ? (
              <>
                <span>
                  <SvgCheckCircle
                    fill={`${coachPublicProfileData?.profile_information?.profileColor}`}
                  />
                </span>
                <p className="ml-3 line-clamp-1 mt-1">
                  {serviceDetails.details.features.feature2}
                </p>
              </>
            ) : null}
          </div>
          <div className="flex items-center">
            {serviceDetails?.details?.features?.feature3 ? (
              <>
                <span>
                  <SvgCheckCircle
                    fill={`${coachPublicProfileData?.profile_information?.profileColor}`}
                  />
                </span>
                <p className="ml-3 line-clamp-1 mt-1">
                  {serviceDetails.details.features.feature3}
                </p>
              </>
            ) : null}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            className={`bg-${coachColor} text-${getTextColor(
              coachColor
            )} w-full`}
            style={{
              backgroundColor: coachColor,
              color: getTextColor(coachColor),
            }}
            onClick={goToEditService}
          >
            <SvgEdit className="mr-2" />
            Edit
          </Button>
          <Button
            className="w-full"
            variant="outlineDynamic"
            style={{
              color: coachColor,
              borderColor: coachColor,
            }}
            onClick={(e) => {
              e.stopPropagation()
              copyShareLink()
            }}
          >
            <SvgShare className="mr-2" />
            Share
          </Button>
        </div>
      </div>
    </div>
  )
}
